import * as React from "react"

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical, SlidersHorizontal } from "lucide-react"

import { cn } from "../../cn"
import { Button } from "../Button/Button"
import { Checkbox } from "../Checkbox/Checkbox"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../DropdownMenu/DropdownMenu"
import { SearchInput } from "../SearchInput/SearchInput"
import { Tooltip } from "../Tooltip/Tooltip"
import type { ColumnDef } from "./types"

export interface TableColumnToggleProps<T> {
  /** The same `columns` passed to the Table — provides ids and header labels. */
  columns: ColumnDef<T>[]
  /** Current visibility map (an absent / `true` entry means visible). */
  columnVisibility: Record<string, boolean>
  /** Receives the next visibility map. Lift this into the Table's matching prop. */
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void
  /**
   * Current column order (ids), mirroring the Table's `columnOrder` prop.
   * Pair with `onColumnOrderChange` to enable drag-to-reorder in the menu; omit
   * both for a visibility-only menu. Lift the state up and pass it to both this
   * and the `Table`.
   */
  columnOrder?: string[]
  /**
   * Receives the next order. Required alongside `columnOrder` to enable
   * reordering; a column opts out with `enableReordering: false`.
   */
  onColumnOrderChange?: (order: string[]) => void
  /**
   * When provided, renders a "Reset to default" item that calls this. The
   * consumer owns what "default" means — typically clearing both the order and
   * visibility overrides.
   */
  onReset?: () => void
  /** Prevent a specific visibility transition, such as exceeding a column cap. */
  isColumnVisibilityDisabled?: (
    column: ColumnDef<T>,
    isVisible: boolean,
  ) => boolean
  /** Show a search field for filtering the column list. */
  searchable?: boolean
  /** Search input placeholder and accessible label. */
  searchPlaceholder?: string
  /** Constrain or otherwise style the portaled dropdown content. */
  contentClassName?: string
  /** Show the full column label in a tooltip when hovering its truncated row. */
  showFullLabelOnHover?: boolean
  /** Trigger label. @default "Columns" */
  label?: string
  /** Render the trigger as a compact icon button with the label in a tooltip. */
  iconOnly?: boolean
  /** Data attributes forwarded to the portaled dropdown content. */
  contentDataAttributes?: {
    [attributeName: `data-${string}`]: string | undefined
  }
}

/**
 * Move `activeId` to `overId`'s slot within `order`. Returns `order` unchanged
 * when either id is absent or they already share a slot. Non-listed columns
 * between the two shift by one, preserving their relative order.
 */
export function reorderColumns(
  order: string[],
  activeId: string,
  overId: string,
): string[] {
  const from = order.indexOf(activeId)
  const to = order.indexOf(overId)
  if (from === -1 || to === -1 || from === to) {
    return order
  }
  return arrayMove(order, from, to)
}

function columnLabel<T>(column: ColumnDef<T>): string {
  return (
    column.headerLabel ??
    (typeof column.header === "string" ? column.header : column.id)
  )
}

/**
 * Dropdown of per-column visibility checkboxes — the management UI for the
 * Table's controlled `columnVisibility`. Lift the visibility state up and pass
 * it (plus `onColumnVisibilityChange`) to both this and the `Table`.
 *
 * Pass `columnOrder` + `onColumnOrderChange` to additionally enable
 * drag-to-reorder: each row gets a grip handle and the list mirrors the current
 * order. Pass `onReset` to offer a "Reset to default" item.
 *
 * Columns with `enableHiding: false` are omitted; the last visible column can't
 * be hidden. The menu stays open while toggling so several columns can be
 * changed at once.
 */
export function TableColumnToggle<T>({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  onReset,
  isColumnVisibilityDisabled,
  searchable = false,
  searchPlaceholder = "Search columns...",
  contentClassName,
  showFullLabelOnHover = false,
  label = "Columns",
  iconOnly = false,
  contentDataAttributes,
}: TableColumnToggleProps<T>) {
  const [searchQuery, setSearchQuery] = React.useState("")
  const reorderable = Boolean(columnOrder && onColumnOrderChange)

  const toggleable = React.useMemo(
    () => columns.filter((col) => col.enableHiding ?? true),
    [columns],
  )

  // In reorder mode the list follows `columnOrder`; ids missing from it trail in
  // declared order. Otherwise the declared order stands.
  const orderedColumns = React.useMemo(() => {
    if (!columnOrder) {
      return toggleable
    }
    const rank = new Map(columnOrder.map((id, index) => [id, index]))
    return [...toggleable].sort(
      (a, b) =>
        (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    )
  }, [toggleable, columnOrder])
  const filteredColumns = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return query
      ? orderedColumns.filter((column) =>
          columnLabel(column).toLowerCase().includes(query),
        )
      : orderedColumns
  }, [orderedColumns, searchQuery])

  const isVisible = (id: string) => columnVisibility[id] ?? true
  const visibleCount = toggleable.filter((col) => isVisible(col.id)).length
  const isDisabled = (column: ColumnDef<T>, visible: boolean) =>
    (visible && visibleCount === 1) ||
    Boolean(isColumnVisibilityDisabled?.(column, visible))

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (
      !over ||
      active.id === over.id ||
      !columnOrder ||
      !onColumnOrderChange
    ) {
      return
    }
    onColumnOrderChange(
      reorderColumns(columnOrder, String(active.id), String(over.id)),
    )
  }

  const trigger = (
    <DropdownMenuTrigger asChild>
      <Button
        variant="secondary"
        icon={iconOnly}
        tooltip={label}
        tooltipProps={{ side: "bottom" }}
      >
        <SlidersHorizontal />
        {!iconOnly && label}
      </Button>
    </DropdownMenuTrigger>
  )

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) {
          setSearchQuery("")
        }
      }}
    >
      {trigger}
      <DropdownMenuContent
        {...contentDataAttributes}
        align="end"
        className={contentClassName}
      >
        <DropdownMenuLabel>
          {reorderable ? "Toggle & reorder columns" : "Toggle columns"}
        </DropdownMenuLabel>
        {searchable && (
          <div className="px-1 pb-1">
            <SearchInput
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onClear={() => setSearchQuery("")}
              onKeyDown={(event) => {
                if (event.key !== "Escape") {
                  event.stopPropagation()
                }
              }}
            />
          </div>
        )}
        <div className="max-h-[min(60vh,28rem)] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {reorderable ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredColumns.map((col) => col.id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredColumns.map((col) => {
                  const checked = isVisible(col.id)
                  return (
                    <SortableColumnRow
                      key={col.id}
                      id={col.id}
                      label={columnLabel(col)}
                      checked={checked}
                      disabled={isDisabled(col, checked)}
                      draggable={col.enableReordering ?? true}
                      showFullLabelOnHover={showFullLabelOnHover}
                      contentDataAttributes={contentDataAttributes}
                      onCheckedChange={(next) =>
                        onColumnVisibilityChange({
                          ...columnVisibility,
                          [col.id]: next,
                        })
                      }
                    />
                  )
                })}
              </SortableContext>
            </DndContext>
          ) : (
            filteredColumns.map((col) => {
              const checked = isVisible(col.id)
              const labelText = columnLabel(col)
              const labelNode = (
                <span className="min-w-0 flex-1 truncate">{labelText}</span>
              )
              return (
                <DropdownMenuCheckboxItem
                  key={col.id}
                  checked={checked}
                  disabled={isDisabled(col, checked)}
                  className="min-w-0"
                  // Keep the menu open so multiple columns can be toggled at once.
                  onSelect={(event) => event.preventDefault()}
                  onCheckedChange={(next) =>
                    onColumnVisibilityChange({
                      ...columnVisibility,
                      [col.id]: next,
                    })
                  }
                >
                  {showFullLabelOnHover ? (
                    <Tooltip
                      content={labelText}
                      side="left"
                      contentDataAttributes={contentDataAttributes}
                    >
                      {labelNode}
                    </Tooltip>
                  ) : (
                    labelNode
                  )}
                </DropdownMenuCheckboxItem>
              )
            })
          )}
          {filteredColumns.length === 0 && (
            <p className="px-2 py-4 text-center text-sm text-coval-text-muted">
              No columns found
            </p>
          )}
        </div>
        {onReset && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              // Keep the menu open so the reset result is visible in place.
              onSelect={(event) => {
                event.preventDefault()
                onReset()
              }}
            >
              Reset to default
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * One reorderable row in the menu: a drag handle, a visibility checkbox, and the
 * column label. Non-reorderable columns (`enableReordering: false`) keep the
 * checkbox but render an inert grip placeholder so rows stay aligned.
 */
function SortableColumnRow({
  id,
  label,
  checked,
  disabled,
  draggable,
  onCheckedChange,
  showFullLabelOnHover,
  contentDataAttributes,
}: {
  id: string
  label: string
  checked: boolean
  disabled: boolean
  draggable: boolean
  onCheckedChange: (next: boolean) => void
  showFullLabelOnHover: boolean
  contentDataAttributes?: {
    [attributeName: `data-${string}`]: string | undefined
  }
}) {
  const reactId = React.useId()
  const checkboxId = `${reactId}-${id}`
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !draggable })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        "flex min-w-0 items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-coval-text",
        isDragging && "z-10 bg-surface-1 opacity-80 shadow-sm",
      )}
    >
      {draggable ? (
        <button
          type="button"
          aria-label={`Reorder ${label}`}
          className="flex-shrink-0 cursor-grab touch-none rounded-sm text-coval-text-muted hover:text-coval-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
      ) : (
        // Reserve the grip's width so checkboxes stay aligned across rows.
        <span className="size-4 flex-shrink-0" aria-hidden />
      )}
      <Checkbox
        id={checkboxId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onCheckedChange(next === true)}
      />
      {showFullLabelOnHover ? (
        <Tooltip
          content={label}
          side="left"
          contentDataAttributes={contentDataAttributes}
        >
          <label
            htmlFor={checkboxId}
            className={cn(
              "min-w-0 flex-1 cursor-pointer select-none truncate",
              disabled && "cursor-default",
            )}
          >
            {label}
          </label>
        </Tooltip>
      ) : (
        <label
          htmlFor={checkboxId}
          className={cn(
            "min-w-0 flex-1 cursor-pointer select-none truncate",
            disabled && "cursor-default",
          )}
        >
          {label}
        </label>
      )}
    </div>
  )
}
